import { useRecoilValue } from 'recoil';
import GraphIcon from '@mui/icons-material/ShowChart';
import BreakdownGraphIcon from '@mui/icons-material/CallSplit';
import PieChartIcon from '@mui/icons-material/PieChart';
import DonutChartIcon from '@mui/icons-material/DonutLarge';
import TableIcon from '@mui/icons-material/TableChart';
import NumberIcon from '@mui/icons-material/LooksOne';
import MarkdownIcon from '@mui/icons-material/FontDownload';
import HexagonIcon from '@mui/icons-material/Hexagon';
import TabIcon from '@mui/icons-material/Tab';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import BootstrapDropdown from 'react-bootstrap/Dropdown';

import { useIsLabEnabled } from 'hooks/useIsLabEnabled';
import * as K from 'common/constants';
import Button from 'components/common/Button/Button';
import DropdownItems from 'components/common/DropdownItems/DropdownItems';
import ELEMENT from 'common/element';
import { IconTextPair } from 'components/common/DropdownItems/DropdownItems';
import { dashboardFilterConfigListState, dashboardTabsState } from 'components/dashboards/dashboards.state';

import './AddPanelDropdown.scss';

type AddPanelDropdownProps = {
  hidePanelDropdown: () => void;
  showAddStandardGraph: () => void;
  showAddBreakdownGraph: () => void;
  showAddPieChart: () => void;
  showAddTablePanel: () => void;
  showAddTabsPanel: () => void;
  showAddNumberPanel: () => void;
  showAddMarkdownPanel: () => void;
  showAddDonutPanel: () => void;
  showAddHoneycombPanel: () => void;
  showAddEditDashboardFilters: () => void;
  hasWriteAccess: boolean;
};

function AddPanelDropdown({
  hidePanelDropdown,
  showAddStandardGraph,
  showAddBreakdownGraph,
  showAddPieChart,
  showAddTablePanel,
  showAddNumberPanel,
  showAddMarkdownPanel,
  showAddDonutPanel,
  showAddHoneycombPanel,
  showAddTabsPanel,
  showAddEditDashboardFilters,
  hasWriteAccess,
}: AddPanelDropdownProps): JSX.Element {
  const isK8sDashboard = useIsLabEnabled(K.LAB.K8S_DASHBOARD);
  const isDashboardFiltersEnabled = useIsLabEnabled(K.LAB.DASHBOARD_FILTERS);
  const tabs = useRecoilValue(dashboardTabsState);
  const dashboardFilterConfigList = useRecoilValue(dashboardFilterConfigListState);

  const items = [
    {
      icon: <GraphIcon className="AddPanelDropdown__Icon" fontSize="small" />,
      clickHandler: showAddStandardGraph,
      testId: ELEMENT.dashboardHeaderAddMenuGraph,
      name: 'Line/Area',
    },
    {
      icon: <BreakdownGraphIcon className="AddPanelDropdown__Icon" fontSize="small" />,
      clickHandler: showAddBreakdownGraph,
      testId: ELEMENT.dashboardHeaderAddMenuBreakdown,
      name: 'Breakdown',
    },
    {
      icon: <PieChartIcon className="AddPanelDropdown__Icon" fontSize="small" />,
      clickHandler: showAddPieChart,
      testId: ELEMENT.dashboardHeaderAddMenuPie,
      name: 'Pie Chart',
    },
    {
      icon: <DonutChartIcon className="AddPanelDropdown__Icon" fontSize="small" />,
      clickHandler: showAddDonutPanel,
      testId: ELEMENT.dashboardHeaderAddMenuDonut,
      name: 'Donut',
    },
    {
      icon: <TableIcon className="AddPanelDropdown__Icon" fontSize="small" />,
      clickHandler: showAddTablePanel,
      testId: ELEMENT.dashboardHeaderAddMenuTable,
      name: 'Table',
    },
    {
      icon: <NumberIcon className="AddPanelDropdown__Icon" fontSize="small" />,
      clickHandler: showAddNumberPanel,
      testId: ELEMENT.dashboardHeaderAddMenuNumber,
      name: 'Number',
    },
  ];

  if (isK8sDashboard) {
    items.push({
      icon: <HexagonIcon className="AddPanelDropdown__Icon" fontSize="small" />,
      clickHandler: showAddHoneycombPanel,
      testId: ELEMENT.dashboardHeaderAddMenuHoneycomb,
      name: 'Honeycomb',
    });
  }
  const addPanelClickHandler = (callback: () => void): void => {
    hidePanelDropdown();
    callback();
  };

  const panelItems = items.map((menuItem) => (
    <div className="AddPanelDropdown__PanelItem" key={menuItem.name}>
      <Button onClick={(): void => addPanelClickHandler(menuItem.clickHandler)} testId={menuItem.testId}>
        {menuItem.icon}
      </Button>
      <div>{menuItem.name}</div>
    </div>
  ));

  const menuSections = [
    [
      {
        children: (
          <IconTextPair leftIcon={<TabIcon fontSize="small" />} label={`${tabs.length > 0 ? 'Edit' : 'Add'} Tabs`} />
        ),
        clickHandler: (): void => {
          hidePanelDropdown();
          showAddTabsPanel();
        },
        testId: ELEMENT.dashboardHeaderAddTabs,
      },
      {
        children: <IconTextPair leftIcon={<MarkdownIcon fontSize="small" />} label="Add Markdown" />,
        clickHandler: (): void => {
          hidePanelDropdown();
          showAddMarkdownPanel();
        },
        testId: ELEMENT.dashboardHeaderAddMenuMarkdown,
      },
    ],
  ];

  if (isDashboardFiltersEnabled && hasWriteAccess) {
    const addEditFilterLabel = dashboardFilterConfigList.length > 0 ? 'Edit Filters' : 'Add Filters';
    menuSections[0].unshift({
      children: <IconTextPair leftIcon={<FilterAltIcon fontSize="small" />} label={addEditFilterLabel} />,
      clickHandler: (): void => {
        showAddEditDashboardFilters();
        hidePanelDropdown();
      },
      testId: ELEMENT.dashboardHeaderAddEditFilters,
    });
  }

  return (
    <div className="Dropdown__Menu">
      <div className="AddPanelDropdown">
        <h3 className="AddPanelDropdown__Title">Add Graphs</h3>
        <div className="AddPanelDropdown__Panels">{panelItems}</div>
        <BootstrapDropdown.Divider className="DropdownItems__Divider" />
        <div className="AddPanelDropdown__MenuItems">
          <DropdownItems sections={menuSections} itemClass="AddPanelDropdown__MenuItems__Item" />
        </div>
      </div>
    </div>
  );
}

export default AddPanelDropdown;
